import {Chart, ArcElement, Element, Plugin, ChartTypeRegistry} from 'chart.js';
import {AnyObject} from 'chart.js/types/basic';

import {CHART_COLORS} from './theme';
import {IMAGE_PREFIX} from './env';

export {Pie} from 'react-chartjs-2';

type nth = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
type Images = Record<nth, Record<'off' | 'on', Promise<HTMLImageElement>>>;

function loadImage(uri: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = uri;
  });
}

const IMAGES: Images = {} as Images;
for (let i = 0, l = 10; i < l; i++) {
  let num = i < 8 ? `0${i + 1}` : '10';
  IMAGES[i as nth] = {
    off: loadImage(`${IMAGE_PREFIX}icon-heart-${num}_off.png`),
    on: loadImage(`${IMAGE_PREFIX}icon-heart-${num}_on.png`)
  };
}

type PieElement = Element<AnyObject, AnyObject> & {
  startAngle?: number,
  endAngle?: number,
  outerRadius?: number
};

function centerOf(element: PieElement) {
  const startAngle = element.startAngle ?? 0;
  const endAngle = element.endAngle ?? 0;
  const outerRadius = element.outerRadius ?? 0;
  const midAngle = (startAngle + endAngle) / 2 + Math.PI; // 何故piだけ足さねばならないのか分かってない
  const midRadius = outerRadius / 2;
  const x = element.x - midRadius * Math.cos(midAngle);
  const y = element.y - midRadius * Math.sin(midAngle);
  return {x, y};
}

function boxWidthOf({text, fontSize}: {text: string, fontSize: number}) {
  const scratch = document.createElement('div');
  scratch.style.fontSize = `${fontSize}px`;
  scratch.style.position = 'absolute';
  scratch.style.visibility = 'hidden';
  scratch.style.zIndex = '-1';
  scratch.textContent = text;
  document.body.append(scratch);
  const textW = Number.parseFloat(window.getComputedStyle(scratch).width);
  scratch.remove();
  return textW;
}

export const NumberLabels: Plugin<keyof ChartTypeRegistry, AnyObject> = {
  id: 'numberlabel',
  afterDatasetDraw: (chart, {meta}) => {
    const canvasW = chart.canvas.clientWidth;
    const fontSize = Math.max((29/288) * canvasW, 18);
    meta.data.forEach((element: PieElement, index: number) => {
      const {x, y} = centerOf(element);
      const text = `${index + 1}`;
      const textW = boxWidthOf({text, fontSize});
      const origFillStyle = chart.ctx.fillStyle;
      chart.ctx.fillStyle = CHART_COLORS[index].text;
      chart.ctx.font = `bold ${fontSize}px sans-serif`
      chart.ctx.fillText(text, x - textW / 2, y);
      chart.ctx.fillStyle = origFillStyle;
    });
  }
};

export const ImageLabels: Plugin<keyof ChartTypeRegistry, AnyObject> = {
  id: "imagelabels",
  afterDatasetDraw: (chart, {meta}, options) => {
    const canvasW = chart.canvas.clientWidth;
    const fontSize = Math.max((26/685) * canvasW, 15);
    const iconW = Math.max((20/685) * canvasW, 9);
    const iconH = iconW * (1742/2009)
    const iconGap = iconW * (800/2009);
    meta.data.forEach((element: PieElement, index: number) => {
      const {x, y} = centerOf(element);
      const dividuals = options.dividuals as {name: string, comfort: Comfort, percentage: number}[];
      const {name, comfort, percentage} = dividuals[index];
      const text = `${name ?? ''}(${percentage}%)`;
      const textW = boxWidthOf({text, fontSize});

      const origFillStyle = chart.ctx.fillStyle;
      chart.ctx.fillStyle = CHART_COLORS[index].text;
      chart.ctx.font = `${fontSize}px sans-serif`;
      chart.ctx.fillText(text, x - textW / 2, y - fontSize);
      chart.ctx.fillStyle = origFillStyle;
      const off = IMAGES[index as nth].off;
      const on = IMAGES[index as nth].on;
      const iconOffset = iconW * 2.5 + iconGap * 2;
      on.then(image => {
        for (let i = 0; i < comfort; i++) {
          const translateX = (iconW + iconGap) * i - iconOffset;
          chart.ctx.drawImage(image, x + translateX, y, iconW, iconH);
        }
      });
      off.then(image => {
        for (let i = 0; i < 5 - comfort; i++) {
          const translateX = (iconW + iconGap) * (comfort + i) - iconOffset;
          chart.ctx.drawImage(image, x + translateX, y, iconW, iconH);
        }
      });
    });
  },
  afterEvent: (chart, {event}, options) => {
    if (event.type !== 'mouseout') {
      return;
    }
    const onHover = options.onHover as ((index: number | undefined) => void) | undefined;
    onHover?.(undefined);
  }
};

Chart.register(ArcElement);
